<template>
  <div class="c3-progress" :class="{ 'c3-progress--clickable': clickable }">
    <div
      class="c3-progress__track"
      :style="{ height: height + 'px' }"
      @click="$emit('click')"
    >
      <div class="c3-progress__fill" :style="fillStyle"></div>
      <div v-if="bStripes" class="c3-progress__over" :style="overStyle"></div>
      <div class="c3-progress__label"><slot /></div>
    </div>
    <p
      v-if="showPercent"
      class="c3-progress__percent"
      :class="{ 'c3-progress__percent--over': percent > 100 }"
    >
      {{ percentLabel }} %
    </p>
  </div>
</template>

<script>
// Platform-wide progress bar (same style as the projects list):
// - "amber" for spend vs. budget, "green" for collections vs. contract.
// - The fill is one flat color that gets more solid as the bar advances
//   (light near 0%, fully solid at 100%).
// - Over 100% the bar stays full and the percentage turns red and bold.
//   Spend bars (amber) also get a striped layer on top, from the left, as
//   wide as the excess over 100% (e.g. 130% -> 30% of the bar), like a
//   second bar on top of the first one.
const COLORS = {
  amber: "255, 179, 0",
  green: "76, 175, 80",
};

export default {
  props: {
    // Percentage; may exceed 100.
    percent: { type: Number, default: 0 },
    color: { type: String, default: "amber" },
    height: { type: [Number, String], default: 20 },
    showPercent: { type: Boolean, default: true },
    clickable: { type: Boolean, default: false },
  },
  computed: {
    clamped() {
      const p = Number(this.percent) || 0;
      return Math.min(Math.max(p, 0), 100);
    },
    bStripes() {
      return this.color === "amber" && (Number(this.percent) || 0) > 100;
    },
    overStyle() {
      const excess = Math.min((Number(this.percent) || 0) - 100, 100);
      return { width: excess + "%" };
    },
    percentLabel() {
      const p = Number(this.percent) || 0;
      return Number.isInteger(p) ? p : p.toFixed(2);
    },
    fillStyle() {
      const rgb = COLORS[this.color] || COLORS.amber;
      // One flat color for the whole fill; it gets more solid as the bar
      // advances (25% opacity near 0%, fully solid at 100%).
      const alpha = 0.25 + 0.75 * (this.clamped / 100);
      return {
        width: this.clamped + "%",
        backgroundColor: `rgba(${rgb}, ${alpha.toFixed(3)})`,
      };
    },
  },
};
</script>

<style scoped>
.c3-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.c3-progress__track {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  border-radius: 40px;
  background: #eceff1;
  overflow: hidden;
}
.c3-progress--clickable .c3-progress__track {
  cursor: pointer;
}
.c3-progress__fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  transition: width 0.3s ease;
}
.c3-progress__over {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.45) 0,
    rgba(255, 255, 255, 0.45) 3px,
    transparent 3px,
    transparent 9px
  );
}
.c3-progress__label {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #212121;
}
.c3-progress__percent {
  flex-shrink: 0;
  margin: 0 !important;
  min-width: 52px;
  text-align: right;
}
.c3-progress__percent--over {
  color: #e53935;
  font-weight: 700;
}
</style>
